import React from "react";
import {
  X,
  Tag,
  Percent,
  IndianRupee,
  ShoppingBag,
  Calendar,
  Clock,
  Users,
  CheckCircle,
  XCircle,
  AlertCircle,
  Gift,
  Layers,
  ShoppingCart,
  CalendarDays,
  Timer,
  Repeat,
  Target,
  Hash,
  FileText,
  Utensils,
  Package,
  Truck,
  TrendingUp,
} from "lucide-react";

// Status configuration
const STATUS_CONFIG = {
  active: { label: "Active", bgColor: "bg-emerald-100", textColor: "text-emerald-700", icon: CheckCircle },
  inactive: { label: "Inactive", bgColor: "bg-slate-100", textColor: "text-slate-700", icon: XCircle },
  scheduled: { label: "Scheduled", bgColor: "bg-blue-100", textColor: "text-blue-700", icon: Clock },
  expired: { label: "Expired", bgColor: "bg-red-100", textColor: "text-red-700", icon: AlertCircle },
};

// Discount type config
const DISCOUNT_TYPE_CONFIG = {
  percentage: { label: "Percentage", icon: Percent, bgColor: "bg-blue-100", textColor: "text-blue-600" },
  fixed: { label: "Fixed Amount", icon: IndianRupee, bgColor: "bg-green-100", textColor: "text-green-600" },
  bogo: { label: "Buy One Get One", icon: Gift, bgColor: "bg-purple-100", textColor: "text-purple-600" },
  complimentary: { label: "Complimentary", icon: Gift, bgColor: "bg-pink-100", textColor: "text-pink-600" },
  bundle: { label: "Bundle Deal", icon: Package, bgColor: "bg-orange-100", textColor: "text-orange-600" },
  tiered: { label: "Tiered", icon: TrendingUp, bgColor: "bg-indigo-100", textColor: "text-indigo-600" },
};

// Get discount status
const getDiscountStatus = (discount) => {
  if (!discount.isActive) return "inactive";
  const now = new Date();
  const startDate = discount.validFrom ? new Date(discount.validFrom) : null;
  const endDate = discount.validUntil ? new Date(discount.validUntil) : null;
  if (endDate && now > endDate) return "expired";
  if (startDate && now < startDate) return "scheduled";
  return "active";
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

// Format time
const formatTime = (timeStr) => {
  if (!timeStr) return null;
  const parts = timeStr.split(":");
  const hours = parseInt(parts[0]);
  const minutes = parts[1];
  const ampm = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes} ${ampm}`;
};

// Info item component - compact
function InfoItem({ icon: Icon, label, value, highlight = false }) {
  return (
    <div className="flex items-center gap-2 py-1.5">
      <Icon size={14} className="text-gray-400 flex-shrink-0" />
      <span className="text-xs text-gray-500">{label}:</span>
      <span className={`text-sm font-medium ${highlight ? "text-teal-600" : "text-gray-900"}`}>
        {value || "—"}
      </span>
    </div>
  );
}

// Badge component
function Badge({ children, variant = "default" }) {
  const variants = {
    default: "bg-gray-100 text-gray-700",
    success: "bg-emerald-100 text-emerald-700",
    warning: "bg-amber-100 text-amber-700",
    info: "bg-blue-100 text-blue-700",
    purple: "bg-purple-100 text-purple-700",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${variants[variant]}`}>
      {children}
    </span>
  );
}

/**
 * DiscountViewModal - Wide layout showing all discount details
 */
export default function DiscountViewModal({ discount, onClose }) {
  if (!discount) return null;

  const status = getDiscountStatus(discount);
  const statusConfig = STATUS_CONFIG[status];
  const StatusIcon = statusConfig?.icon || CheckCircle;

  const typeConfig = DISCOUNT_TYPE_CONFIG[discount.discountType?.toLowerCase()] || DISCOUNT_TYPE_CONFIG.percentage;
  const TypeIcon = typeConfig.icon;

  // Parse tier config
  const tierSlabs = React.useMemo(() => {
    if (!discount.tierConfig) return [];
    try {
      const parsed = typeof discount.tierConfig === "string" ? JSON.parse(discount.tierConfig) : discount.tierConfig;
      return Array.isArray(parsed) ? parsed.sort((a, b) => (a.minAmount || 0) - (b.minAmount || 0)) : [];
    } catch { return []; }
  }, [discount.tierConfig]);

  // Format discount value
  const formatDiscountValue = () => {
    const type = discount.discountType?.toLowerCase();
    if (type === "percentage") return `${discount.discountValue}%`;
    if (type === "fixed") return `₹${discount.discountValue}`;
    if (type === "bogo") return `Buy ${discount.buyQuantity || 1} Get ${discount.getQuantity || 1} Free`;
    if (type === "complimentary") return `Buy ${discount.buyQuantity || 1} Get ${discount.getQuantity || 1} Free`;
    if (type === "bundle") return `Combo @ ₹${discount.bundlePrice || 0}`;
    if (type === "tiered" && tierSlabs.length > 0) {
      return tierSlabs.map((s) => `${s.value}%`).join(" / ");
    }
    return discount.discountValue;
  };

  // Format applies to
  const getAppliesToBadge = () => {
    const appliesTo = discount.appliesTo?.toLowerCase();
    switch (appliesTo) {
      case "bill": return { label: "Entire Bill", variant: "success" };
      case "item": return { label: "Specific Items", variant: "info" };
      case "category": return { label: "Categories", variant: "purple" };
      default: return { label: appliesTo || "Bill", variant: "default" };
    }
  };

  // Format days of week
  const formatDaysOfWeek = () => {
    if (!discount.daysOfWeek || discount.daysOfWeek.length === 0) return "All Days";
    if (discount.daysOfWeek.length === 7) return "All Days";
    return discount.daysOfWeek.map((d) => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(", ");
  };

  // Format order types with icons
  const getOrderTypeBadges = () => {
    if (!discount.orderTypes || discount.orderTypes.length === 0) {
      return [{ label: "All Types", icon: ShoppingCart }];
    }
    return discount.orderTypes.map((t) => {
      switch (t.toLowerCase()) {
        case "dine_in": return { label: "Dine-In", icon: Utensils };
        case "takeaway": return { label: "Takeaway", icon: Package };
        case "delivery": return { label: "Delivery", icon: Truck };
        default: return { label: t, icon: ShoppingCart };
      }
    });
  };

  const appliesToInfo = getAppliesToBadge();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${typeConfig.bgColor}`}>
                <TypeIcon size={28} className={typeConfig.textColor} />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-bold">{discount.name}</h2>
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${statusConfig.bgColor} ${statusConfig.textColor}`}>
                    <StatusIcon size={12} />
                    {statusConfig.label}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-1">
                  {discount.code && (
                    <span className="text-sm font-mono bg-white/20 px-2 py-0.5 rounded">
                      {discount.code}
                    </span>
                  )}
                  <span className="text-xs text-teal-200">ID: {discount.id}</span>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content - Grid Layout */}
        <div className="p-6">
          {/* Description */}
          {discount.description && (
            <div className="mb-5 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-600">{discount.description}</p>
            </div>
          )}

          {/* Main Grid - 3 columns */}
          <div className="grid grid-cols-3 gap-6">
            {/* Column 1: Discount Details */}
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Tag size={12} />
                Discount Details
              </h3>
              <div className="space-y-1">
                <div className="flex items-center justify-between py-2 border-b border-gray-200">
                  <span className="text-xs text-gray-500">Type</span>
                  <Badge variant="info">{typeConfig.label}</Badge>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-200">
                  <span className="text-xs text-gray-500">Value</span>
                  <span className="text-lg font-bold text-teal-600">{formatDiscountValue()}</span>
                </div>
                {discount.maxDiscountAmount && (
                  <div className="flex items-center justify-between py-2 border-b border-gray-200">
                    <span className="text-xs text-gray-500">Max Discount</span>
                    <span className="text-sm font-semibold text-gray-900">₹{discount.maxDiscountAmount}</span>
                  </div>
                )}
                <div className="flex items-center justify-between py-2">
                  <span className="text-xs text-gray-500">Applies To</span>
                  <Badge variant={appliesToInfo.variant}>{appliesToInfo.label}</Badge>
                </div>
              </div>
            </div>

            {/* Column 2: Conditions */}
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Target size={12} />
                Conditions
              </h3>
              <div className="space-y-1">
                <div className="flex items-center justify-between py-2 border-b border-gray-200">
                  <span className="text-xs text-gray-500">Min Bill</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {discount.minBillAmount > 0 ? `₹${discount.minBillAmount}` : "No minimum"}
                  </span>
                </div>
                <div className="py-2 border-b border-gray-200">
                  <span className="text-xs text-gray-500 block mb-1.5">Order Types</span>
                  <div className="flex flex-wrap gap-1">
                    {getOrderTypeBadges().map((ot, i) => (
                      <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-gray-200 rounded text-xs text-gray-700">
                        <ot.icon size={10} />
                        {ot.label}
                      </span>
                    ))}
                  </div>
                </div>
                {/* Show menu items with names */}
                {discount.menuItems?.length > 0 && (
                  <div className="py-2 border-b border-gray-200">
                    <span className="text-xs text-gray-500 block mb-1.5">Applicable Items ({discount.menuItems.length})</span>
                    <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                      {discount.menuItems.map((item) => (
                        <span key={item.id} className="inline-flex items-center px-2 py-0.5 bg-cyan-50 border border-cyan-200 rounded text-xs text-cyan-700">
                          {item.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {/* Show categories with names */}
                {discount.categories?.length > 0 && (
                  <div className="py-2 border-b border-gray-200">
                    <span className="text-xs text-gray-500 block mb-1.5">Applicable Categories ({discount.categories.length})</span>
                    <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                      {discount.categories.map((cat) => (
                        <span key={cat.id} className="inline-flex items-center px-2 py-0.5 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                          {cat.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {/* Fallback: Show IDs if names not available but IDs exist */}
                {!discount.menuItems?.length && discount.idMenuItems?.length > 0 && (
                  <div className="flex items-center justify-between py-2 border-b border-gray-200">
                    <span className="text-xs text-gray-500">Items</span>
                    <Badge>{discount.idMenuItems.length} item(s)</Badge>
                  </div>
                )}
                {!discount.categories?.length && discount.idCategories?.length > 0 && (
                  <div className="flex items-center justify-between py-2 border-b border-gray-200">
                    <span className="text-xs text-gray-500">Categories</span>
                    <Badge>{discount.idCategories.length} category(ies)</Badge>
                  </div>
                )}
                {!discount.menuItems?.length && !discount.categories?.length && !discount.idMenuItems?.length && !discount.idCategories?.length && (
                  <div className="flex items-center justify-between py-2">
                    <span className="text-xs text-gray-500">Scope</span>
                    <span className="text-sm text-gray-600">All items</span>
                  </div>
                )}
              </div>
            </div>

            {/* Column 3: Validity & Usage */}
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Calendar size={12} />
                Validity & Usage
              </h3>
              <div className="space-y-1">
                <div className="flex items-center justify-between py-2 border-b border-gray-200">
                  <span className="text-xs text-gray-500">Valid From</span>
                  <span className="text-sm font-medium text-gray-900">{formatDate(discount.validFrom)}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-200">
                  <span className="text-xs text-gray-500">Valid Until</span>
                  <span className="text-sm font-medium text-gray-900">{formatDate(discount.validUntil)}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-200">
                  <span className="text-xs text-gray-500">Days</span>
                  <span className="text-sm font-medium text-gray-900">{formatDaysOfWeek()}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-200">
                  <span className="text-xs text-gray-500">Time</span>
                  <span className="text-sm font-medium text-gray-900">
                    {discount.startTime && discount.endTime
                      ? `${formatTime(discount.startTime)} - ${formatTime(discount.endTime)}`
                      : "All Day"}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-200">
                  <span className="text-xs text-gray-500">Usage</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {discount.usageCount || 0}
                    {discount.usageLimit ? ` / ${discount.usageLimit}` : " (Unlimited)"}
                  </span>
                </div>
                {discount.usagePerCustomer && (
                  <div className="flex items-center justify-between py-2">
                    <span className="text-xs text-gray-500">Per Customer</span>
                    <span className="text-sm font-medium text-gray-900">{discount.usagePerCustomer} time(s)</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* BOGO Details - Only if applicable */}
          {discount.discountType?.toLowerCase() === "bogo" && (
            <div className="mt-5 bg-purple-50 rounded-xl p-4 border border-purple-200">
              <h3 className="text-xs font-bold text-purple-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Gift size={12} />
                BOGO Details
              </h3>
              <div className="flex items-center gap-8">
                <div className="flex items-center gap-2">
                  <ShoppingBag size={16} className="text-purple-500" />
                  <span className="text-sm text-gray-600">Buy:</span>
                  <span className="text-lg font-bold text-purple-700">{discount.buyQuantity || 1}</span>
                </div>
                <div className="text-gray-400">→</div>
                <div className="flex items-center gap-2">
                  <Gift size={16} className="text-purple-500" />
                  <span className="text-sm text-gray-600">Get Free:</span>
                  <span className="text-lg font-bold text-purple-700">{discount.getQuantity || 1}</span>
                </div>
              </div>
            </div>
          )}

          {/* Complimentary Details */}
          {discount.discountType?.toLowerCase() === "complimentary" && (
            <div className="mt-5 bg-pink-50 rounded-xl p-4 border border-pink-200">
              <h3 className="text-xs font-bold text-pink-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Gift size={12} />
                Complimentary Details
              </h3>
              <div className="flex items-center gap-8">
                <div className="flex items-center gap-2">
                  <ShoppingBag size={16} className="text-pink-500" />
                  <span className="text-sm text-gray-600">Buy:</span>
                  <span className="text-lg font-bold text-pink-700">{discount.buyQuantity || 1}</span>
                </div>
                <div className="text-gray-400">→</div>
                <div className="flex items-center gap-2">
                  <Gift size={16} className="text-pink-500" />
                  <span className="text-sm text-gray-600">Get Free:</span>
                  <span className="text-lg font-bold text-pink-700">{discount.getQuantity || 1}</span>
                </div>
              </div>
              {discount.freeItems?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-pink-200">
                  <span className="text-xs text-gray-500 block mb-1.5">Free Items</span>
                  <div className="flex flex-wrap gap-1">
                    {discount.freeItems.map((item) => (
                      <span key={item.id} className="inline-flex items-center px-2 py-0.5 bg-pink-100 border border-pink-300 rounded text-xs text-pink-700">
                        {item.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Bundle Details */}
          {discount.discountType?.toLowerCase() === "bundle" && (
            <div className="mt-5 bg-orange-50 rounded-xl p-4 border border-orange-200">
              <h3 className="text-xs font-bold text-orange-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Package size={12} />
                Bundle Details
              </h3>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Combo Price:</span>
                <span className="text-lg font-bold text-orange-700">₹{discount.bundlePrice || 0}</span>
              </div>
            </div>
          )}

          {/* Tiered Discount Details */}
          {discount.discountType?.toLowerCase() === "tiered" && tierSlabs.length > 0 && (
            <div className="mt-5 bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <h3 className="text-xs font-bold text-indigo-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                <TrendingUp size={12} />
                Tier Slabs
              </h3>
              <div className="grid grid-cols-1 gap-2">
                {tierSlabs.map((slab, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-indigo-100">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-[10px] font-bold">
                        {idx + 1}
                      </span>
                      <span className="text-xs text-gray-600">
                        Bill ≥ <span className="font-semibold text-gray-900">₹{slab.minAmount}</span>
                      </span>
                    </div>
                    <span className="text-sm font-bold text-indigo-600">{slab.value}% off</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-200 px-6 py-3 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
